import React, {Fragment} from 'react';
import {Editor} from 'slate-react';
import {Value} from 'slate';
import {schema, importFromHtml, exportToHtml} from './ContentFormatter';
import Toolbar from "../toolbar";
import Icon from "../Icon";
import Modal from "../../layout/Modal";
import classNames from 'classnames';
import debounce from "lodash-es/debounce";
import {
    FigureNode,
    CodeNode,
    VideoButton,
    VideoNode,
    LinkNode,
    VariableNode,
    BlockquoteNode,
    ParagraphNode,
    BreakNode,
    SpanNode,
    EditorTag,
    HeaderNode,
    HeaderEdit,
    ListNode,
    ListEdit,
    Wrapper,
    Table,
    Tbody,
    Tr,
    Td,
    Center,
    Style,

    BoldMark,
    ItalicMark,
    UnderlineMark,
    StrikelineMark,

    BoldButton,
    ItalicButton,
    BlockquoteButton,
    LinkButton,
    UnlinkButton,
    VariableButton,
    EmailTemplateButton,
    UnderlineButton,
    StrikelineButton,
    InsertOnEnterPlugin
} from './plugins';
import isEqual from "lodash-es/isEqual";
import isHotkey from 'is-hotkey';
import Button from "../Button";
import FigureEditor from "../../editors/figureEditor";
import FragmentEditor, {ChildFragmentEditor} from "../../editors/FragmentEditor";
import PropTypes from "prop-types";
import {isTrue} from "../../../utils/other";
import StyleInjector from "../../layout/StyleInjector";

const fullToolbar = ['bold', 'italic', 'underline', 'strike', 'figure', /*'align-left', 'align-center', 'align-right',*/ 'link', 'unlink', 'video', /*'lists', */'header', 'blockquote', 'full-screen'];
const defaultToolbar = ['bold', 'italic', 'underline', 'strike', 'figure', /*'align-left', 'align-center', 'align-right',*/ 'link', 'unlink', 'video', /*'lists', */'header', 'blockquote', 'full-screen'];
const emptyToolbar = [];

const isBoldHotkey = isHotkey('mod+b');
const isItalicHotkey = isHotkey('mod+i');

const plugins = [
    // https://github.com/ianstormtaylor/slate-plugins/tree/master/packages/slate-soft-break
    {
        onKeyDown(event, change, next) {
            if (event.key !== 'Enter' || event.shiftKey === false) return next();
            return change.insertText('\n');
        }
    },
    // https://github.com/ianstormtaylor/slate-plugins/tree/master/packages/slate-collapse-on-escape
    {
        onKeyDown(event, change, next) {
            const { value } = change;
            const { selection } = value;
            if (event.key !== 'Escape') return next();
            if (selection.isCollapsed) return next();
            change.moveToEnd();
        },
    }
];

const disabledEnterPlugins = [
    // https://github.com/ianstormtaylor/slate-plugins/tree/master/packages/slate-soft-break
    {
        onKeyDown(event, change, next) {
            if (event.key === 'Enter') return change;
            if (event.key !== 'Enter' || event.shiftKey === false) return next();
            return change.insertText('\n');
        }
    },
    // https://github.com/ianstormtaylor/slate-plugins/tree/master/packages/slate-collapse-on-escape
    {
        onKeyDown(event, change, next) {
            const { value } = change;
            const { selection } = value;
            if (event.key !== 'Escape') return next();
            if (selection.isCollapsed) return next();
            change.moveToEnd();
        },
    }
];
const SimpleRenderer = (props) => {
    const Tag = props.node.type;
    return <Tag {...props.attributes}>{props.children}</Tag>
}

class EditorField extends React.Component {

    static propTypes = {
        value: PropTypes.any,
        style: PropTypes.any,
        name: PropTypes.string,
        label: PropTypes.string,
        height: PropTypes.string,
        component: PropTypes.string,
        componentId: PropTypes.any,
        wrapperClass: PropTypes.any,
        wrapperId: PropTypes.string,
        toolbar: PropTypes.any,
        disabled: PropTypes.oneOf([true, false, "true", "false", "disabled"]),
        saveChanges: PropTypes.func,
        import: PropTypes.func,
        export: PropTypes.func,
        tagRender: PropTypes.func,
        placeholder: PropTypes.string,
    }

    constructor(props) {
        super(props);
        this.state = {
            html: props.value,
            value: this.loadContent(props.value),
            modal: false,
            modalTitle: null,
            modalBody: null,
            modalButtons: [],
            modalClose: null,
            editorId: "slate-" + new Date().getTime(),
            fullScreen: false,
        };
        this.saveDebounce = debounce((html) => {
            this.saveChanges(this.props.name, html);
        }, 300);
    }

    loadContent = (v) => {
        v = v || [];
        let content;
        if (v.length > 0) {
            if (!v.toLowerCase().startsWith('<editor>')) {
                v = '<editor>' + v;
            }
            if (!v.toLowerCase().endsWith('</editor>')) {
                v = v + '</editor>';
            }
            content = (this.props.import || importFromHtml)(v);
        } else {
            content = (this.props.import || importFromHtml)('<editor><p /></editor>');
        }
        return Value.fromJSON({...content});
    }

    saveChanges = (field, value) => {
        if (this.props.saveChanges) {
            this.props.saveChanges(field, value);
        }
    }

    setNodeByKey = async (key, data) => {
        const change = this.editor.setNodeByKey(key, data);
        this.saveEditing(change);
    }

    removeNodeByKey = async (key) => {
        const change = this.editor.removeNodeByKey(key);
        this.saveEditing(change);
    }

    componentDidUpdate(prevProps, prevState, prevContext) {
        this.onBlurTimeout && clearTimeout(this.onBlurTimeout);
        this.saveDebounce && this.saveDebounce.cancel();
        if (this.props.value + "" !== "" + prevProps.value && this.props.value + "" !== "" + this.state.html) {
            const currentHtml = this.formatter((this.props.export || exportToHtml)(this.editor.value));
            let html = this.props.value;
            if (!isEqual(currentHtml, html)) {
                this.setState({html, value: this.loadContent(this.props.value)});
            }
        }
    }

    componentWillUnmount() {
        this.onBlurTimeout && clearTimeout(this.onBlurTimeout);
        this.saveDebounce && this.saveDebounce.cancel();
    }

    formatter = (html) => {
        const parsed = new DOMParser().parseFromString("<body>"  + html + "</body>", 'text/xml');
        html = parsed.getElementsByTagName("body")[0].innerHTML;
        if (this.props.formatter) {
            html = this.props.formatter(html);
        }
        const c = html.replace(/\s+/g, '');
        if (c === "<p/>" || c === "<p></p>") {
            html = null;
        }
        return html;
    }

    // https://github.com/ianstormtaylor/slate/issues/2434#issuecomment-448651164
    onBlur = (event, editor, next) => {
        next();
        this.onBlurTimeout = setTimeout(() => this.saveChanges(this.props.name, this.state.html), 0);
    }

    promisedSetState = (newState) => {
        return new Promise((resolve) => {
            this.setState(newState, () => {
                resolve()
            });
        });
    }

    onChange = ({value}) => {
        const html = this.formatter((this.props.export || exportToHtml)(this.editor.value));
        this.setState({value, html}, () => this.saveDebounce(html));
    }

    saveEditing = ({value}) => {
        const html = this.formatter((this.props.export || exportToHtml)(value));
        if (html + "" !== "" + this.state.html) {
            this.setState({html, value}, () => {
                this.saveChanges(this.props.name, html);
            });
        }
    }

    toggleMark = (type) => {
        const editor = this.editor
            .toggleMark(type)
            .focus();
        this.saveEditing(editor);
    }

    _onImageClick = () => {
        const imgState = this.state.addImage === "on" ? "off" : "on";
        this.setState({addImage: imgState});
    }

    _onAlignLeftClick = () => {
        //this.onChange(RichUtils.toggleInlineStyle(this.state.editorState,'ITALIC'));
    }

    _onAlignCenterClick = () => {
        //this.onChange(RichUtils.toggleInlineStyle(this.state.editorState,'ITALIC'));
    }

    _onAlignRightClick = () => {
        //this.onChange(RichUtils.toggleInlineStyle(this.state.editorState,'ITALIC'));
    }

    showModal = (modalTitle, modalBody, modalButtons, modalClose) => {
        this.setState({modal: true, modalTitle, modalBody, modalButtons, modalClose});
    }

    closeModal = (btn) => {
        if (this.state.modalClose) this.state.modalClose(btn);
        this.setState({modal: false, modalTitle: null, modalBody: null, modalButtons: [], modalClose: null});
    }

    insertImage = (img) => {
        const data = {};
        if (img.linktype) {
            data.linktype = img.linktype;
            data.linkvalue = img.linkvalue;
        }
        if (img.orientation === "landscape") {
            data.imgstyle = "post_full";
            data.align = "center";
        } else {
            data.imgstyle = "post_side";
            data.align = "left";
        }
        const editor = this.editor
            .insertInline({
                type: 'figure',
                data: {id: img.figure_id, presetdata: data}
            })
            .focus();
        this.saveEditing(editor);
    }

    renderBlock = (props) => {
        props = {...props,
            onChange:this.onChange,
            saveEditing: this.saveEditing,
            onBlur: this.onBlur,
            value: this.state.value,
            component_type: this.props.component,
            component_id: this.props.componentId,
        };
        const def = (props) => {
            switch (props.node.type) {
                case 'editor':
                    return EditorTag(props, this.state.editorId, this.props.wrapperClass, this.props.wrapperId);
                case 'blockquote':
                    return <BlockquoteNode {...props} />;
                case 'paragraph':
                    return <ParagraphNode {...props} />;
                case 'header':
                    return HeaderNode(props.node.data.get("tagname"), props);
                case 'ul':
                    return ListNode('ul', props);
                case 'ol':
                    return ListNode('ol', props);
                case 'wrapper':
                    return Wrapper(props);
                case 'code':
                    return <CodeNode {...props} />;
                case 'inline-css':
                    return <Style {...props} />;
                case 'table':
                    return <Table {...props} />;
                case 'tbody':
                    return <Tbody {...props} />;
                case 'div':
                    return <div>{props.children}</div>;
                case 'tr':
                    return <Tr {...props} />;
                case 'td':
                    return <Td {...props} />;
                case 'center':
                    return <Center {...props}/>;
                default:
                    return SimpleRenderer(props);
            }
        }
        return (this.props.tagRender || ((props) => {
            return def(props);
        }))(props, def);
    }

    renderInline = (props) => {
        props = {...props,
            onChange:this.onChange,
            saveEditing: this.saveEditing,
            onBlur: this.onBlur,
            value: this.state.value,
            component_type: this.props.component,
            component_id: this.props.componentId,
        };
        const def = (props) => {
            switch (props.node.type) {
                case 'break':
                    return <BreakNode />;
                case 'variable':
                    return <VariableNode {...props}>{props.children}</VariableNode>;
                case 'span':
                    return <SpanNode {...props}>{props.children}</SpanNode>;
                case 'link':
                    return <LinkNode {...props}>{props.children}</LinkNode>;
                case 'video':
                    return <VideoNode {...props}>{props.children}</VideoNode>;
                case 'figure':
                    return <FigureNode {...props} onChange={this.onChange} saveEditing={this.saveEditing} onBlur={this.onBlur} value={this.state.value}/>;
            }
        }
        return (this.props.tagRender || ((props) => {
            return def(props);
        }))(props, def);
    }

    renderMark = (props) => {
        props = {...props,
            onChange:this.onChange,
            saveEditing: this.saveEditing,
            onBlur: this.onBlur,
            value: this.state.value,
            component_type: this.props.component,
            component_id: this.props.componentId,
        };
        const def = (props) => {
            switch (props.mark.type) {
                case 'bold':
                    return <BoldMark {...props} />;
                case 'italic':
                    return <ItalicMark {...props} />;
                case 'underline':
                    return <UnderlineMark {...props} />;
                case 'strike':
                    return <StrikelineMark {...props} />;
            }
        }
        return (this.props.tagRender || ((props) => {
            return def(props);
        }))(props, def);
    }

    renderButton = (btn, key) => {
        if (btn instanceof Function) {
            return btn({
                key: key,
                onChange: this.onChange,
                saveEditing: this.saveEditing,
                editor: this.editor,
                value: this.state.value,
                disabled: this.props.disabled
            });
        }
        switch (btn) {
            case 'bold':
                return <BoldButton
                    key={key}
                    onClick={this.toggleMark}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'italic':
                return <ItalicButton
                    key={key}
                    onClick={this.toggleMark}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'underline':
                return <UnderlineButton
                    key={key}
                    onClick={this.toggleMark}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'strike':
                return <StrikelineButton
                    key={key}
                    onClick={this.toggleMark}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'header':
                return <HeaderEdit
                    key={key}
                    onChange={this.onChange}
                    saveEditing={this.saveEditing}
                    editor={this.editor}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'lists':
                return <ListEdit
                    key={key}
                    onChange={this.onChange}
                    saveEditing={this.saveEditing}
                    editor={this.editor}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'blockquote':
                return <BlockquoteButton
                    key={key}
                    onChange={this.onChange}
                    saveEditing={this.saveEditing}
                    editor={this.editor}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'video':
                return <VideoButton
                    key={key}
                    onChange={this.onChange}
                    saveEditing={this.saveEditing}
                    editor={this.editor}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'link':
                return <LinkButton
                    key={key}
                    onChange={this.onChange}
                    saveEditing={this.saveEditing}
                    editor={this.editor}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'unlink':
                return <UnlinkButton
                    key={key}
                    onChange={this.onChange}
                    saveEditing={this.saveEditing}
                    editor={this.editor}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'variable':
                return <VariableButton
                    key={key}
                    onChange={this.onChange}
                    saveEditing={this.saveEditing}
                    editor={this.editor}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'figure':
                return <Button
                    disabled={!(this.props.component && this.props.componentId) || this.props.disabled}
                    key={key}
                    tooltip="Kép beszúrás"
                    icon="image"
                    color={this.state.addImage === "on" ? "primary" : "default"}
                    onClick={this._onImageClick}
                />;
            case 'align-left':
                return <div key={key} className="button" onClick={this._onAlignLeftClick}><Icon
                    name="align-left"/></div>;
            case 'align-center':
                return <div key={key} className="button" onClick={this._onAlignCenterClick}><Icon
                    name="align-center"/></div>;
            case 'align-right':
                return <div key={key} className="button" onClick={this._onAlignRightClick}><Icon
                    name="align-right"/></div>;
            case 'email-template':
                return <EmailTemplateButton
                    key={key}
                    saveChanges={(html) => this.saveChanges(this.props.name, html)}
                    editor={this.editor}
                    value={this.state.value}
                    disabled={this.props.disabled}
                />;
            case 'full-screen':
                return <Button
                    key={key}
                    tooltip="Teljes képernyős mód"
                    className="pull-right"
                    color="default"
                    onClick={() => this.setState({fullScreen: !this.state.fullScreen})}
                    icon={{lib: "fas", name: "compress"}}
                />;
        }
    }

    onKeyDown = (event, editor, next) => {
        if (isBoldHotkey(event)) {
            event.preventDefault();
            editor.toggleMark('bold');
        } else if (isItalicHotkey(event)) {
            event.preventDefault();
            editor.toggleMark('italic');
        } else {
            return next();
        }
    }

    /*loadImage = (selection) => {
        console.log(selection);
        this.setState({image: selection});
    }*/

    isFullScreen = (value) => {
        return value === 'on' || isTrue(value);
    }

    render() {
        let style = {};
        if (this.props.label === false) {
            style.display = "none";
        }

        const toolbar = this.props.toolbar || defaultToolbar;
        return <Fragment>
            <label htmlFor={this.props.name} className="control-label" style={style}>{this.props.label} {this.props.required && <span className="invalid-feedback">*</span>}</label>
            <div className={classNames("rt-editor", {"full-screen": this.isFullScreen(this.state.fullScreen)}, this.props.className)}>
                <Toolbar className="rt-editor-toolbar">
                    {toolbar.map((btn, key) => this.renderButton(btn, key))}
                </Toolbar>
                {this.state.addImage === "on" && this.props.component && this.props.componentId ?
                    <FigureEditor
                        component={this.props.component}
                        componentId={this.props.componentId}
                        list={ChildFragmentEditor}
                        editor={() => <div/>}
                        selection={null}
                        values={{}}
                        loadForm={this.insertImage}
                        isLoading={this.props.disabled}
                        pageSize={0}
                    />
                    : null
                }
                <Modal state={this.state.modal ? "on" : "off"} header={this.state.modalTitle}
                       buttons={this.state.modalButtons} onClose={this.closeModal}>
                    {this.state.modalBody}
                </Modal>
                <div className="rt-editor-input" style={{height: this.props.height}}>
                    <StyleInjector style={this.props.style} id={"rt-editor-" + this.state.editorId} />
                    <Editor
                        ref={(editor) => this.editor = editor}
                        value={this.state.value}
                        schema={schema}
                        plugins={this.props.disableNewLine ? disabledEnterPlugins : plugins}
                        onChange={this.onChange}
                        onKeyDown={this.onKeyDown}
                        renderBlock={this.renderBlock}
                        renderMark={this.renderMark}
                        renderInline={this.renderInline}
                        onBlur={this.onBlur}
                        placeholder={this.props.placeholder}
                        readOnly={this.props.disabled === "true" || this.props.disabled === "disabled" || this.props.disabled}
                    />
                </div>
            </div>
            {this.props.error && <div className="invalid-feedback">{this.props.error}</div>}
        </Fragment>;
    }
}

export default EditorField;

export {EditorField, defaultToolbar, emptyToolbar, fullToolbar};